import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { Icon } from '@iconify/react';
import { 
  formatOcpiDate, 
  calculateOcpiDuration, 
  formatOcpiCurrency,
  formatOcpiEnergy
} from '../../../utils/formatTime';
import { 
  statusColorMap
} from '../constants/ocpi.constant';
import { useTheme } from '@mui/material';
import { OCPI_COMMANDS } from 'src/utils/ocpi.constants';
import MuiConfirmationDialog from '../../@material-extend/MuiConfirmationDialog';

// Add CSS for spinning animation
const spinAnimation = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
`;

const SessionCard = ({ session, isOngoing = false, onStopSession, showCdrDetails = false }) => {
  const theme = useTheme();
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  const handleStopSession = async () => {
    if (!onStopSession) return;
    
    setIsStopping(true);
    try {
      await onStopSession({
        session_id: session.id,
        command_type: OCPI_COMMANDS.STOP_SESSION,
        country_code: session.countryCode,
        party_id: session.partyId,
      });
      setShowStopDialog(false);
    } catch (error) {
      console.error('Error stopping session:', error);
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <>
      <style>{spinAnimation}</style>
      <Card 
      sx={{ 
        mb: 2,
        border: isOngoing ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
        position: 'relative',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease'
        }
      }}
    > 
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {showCdrDetails ? 'CDR #' + (session.cdrId || session.id) : `Session #${session.id}`}
              </Typography>
              {showCdrDetails && session.cdrToken?.type && (
                <Chip
                  label={session.cdrToken.type} 
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              EVSE ID: {session.evseId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Country: {session.countryCode} | Location Id: {session.locationId} | Party Id: {session.partyId}
            </Typography>
            {session.locationName && (
              <Typography variant="body2" color="text.secondary">
                Location: {session.locationName}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={session.status}
              color={statusColorMap[session.status].color}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: statusColorMap[session.status].bgcolor,
                color: statusColorMap[session.status].text,
              }}
            />
            {isOngoing && (
              <Tooltip title="Active Session">
                <Icon 
                  icon="eva:radio-button-on-fill" 
                  style={{ color: '#4caf50', fontSize: '20px' }}
                  className='pulse'
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Start Time
              </Typography>
              <Typography variant="body1">
                {formatOcpiDate(session.startTime)}
              </Typography>
            </Box>
            
            {!isOngoing && session.endTime && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  End Time
                </Typography>
                <Typography variant="body1">
                  {formatOcpiDate(session.endTime)}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Energy Consumed
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                {formatOcpiEnergy(session.kwhConsumed)}
              </Typography>
            </Box>

            {!isOngoing && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1">
                  {calculateOcpiDuration(session.startTime, session.endTime)}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {session.totalCost && (
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Cost
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                {formatOcpiCurrency(session.totalCost.incl_vat, session.currency)} (incl. VAT)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatOcpiCurrency(session.totalCost.excl_vat, session.currency)} (excl. VAT)
              </Typography>
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* View Details Button - Only for CDR records */}
            {showCdrDetails && (
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => setShowDetailsDialog(true)}
                startIcon={<Icon icon="eva:eye-fill" />}
                sx={{ 
                  py: 1,
                  px: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto'
                }}
              >
                View Details
              </Button>
            )}
            
            {/* Stop Session Button - Only for ongoing sessions */}
            {isOngoing && (
              <Button
                variant="contained"
                color="error"
                size="medium"
                onClick={() => setShowStopDialog(true)}
                startIcon={<Icon icon="eva:stop-circle-fill" />}
                sx={{ 
                  py: 1,
                  px: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto'
                }}
              >
                Stop Session
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>

      {/* Stop Session Confirmation Dialog */}
      <MuiConfirmationDialog
        open={showStopDialog}
        onClose={() => setShowStopDialog(false)}
        title="Stop Charging Session"
        severity="warning"
        contentText="Are you sure you want to stop this charging session?"
        okText="Stop Session"
        closeText="Cancel"
        isLoading={isStopping}
        onOk={handleStopSession}
        okButtonProps={{
          color: "error",
          startIcon: <Icon icon="eva:stop-circle-fill" />
        }}
        content={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Session ID:</strong> {session.sessionId}<br />
              <strong>EVSE ID:</strong> {session.evseId}<br />
              <strong>Energy Consumed:</strong> {formatOcpiEnergy(session.kwhConsumed)}<br />
              <strong>Start Time:</strong> {formatOcpiDate(session.startTime)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action will immediately terminate the charging session and may affect the user experience.
            </Typography>
          </Box>
        }
      />

      {/* View Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            Session Details
          </Typography>
          <IconButton
            onClick={() => setShowDetailsDialog(false)}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.light + '20',
                color: theme.palette.primary.dark
              }
            }}
          >
            <Icon icon="eva:close-fill" fontSize={20} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
                Session Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Session ID:</strong> {session.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>EVSE ID:</strong> {session.evseId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Connector ID:</strong> {session.connectorId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> {session.status}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ mb: 2, color: theme.palette.info.main }}>
                Timing Details
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Start Time:</strong> {formatOcpiDate(session.startTime)}
                </Typography>
                {session.endTime && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>End Time:</strong> {formatOcpiDate(session.endTime)}
                  </Typography>
                )}
                {!isOngoing && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Duration:</strong> {calculateOcpiDuration(session.startTime, session.endTime)}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ mb: 2, color: theme.palette.warning.main }}>
                Energy & Cost
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Energy Consumed:</strong> {formatOcpiEnergy(session.kwhConsumed)}
                </Typography>
                {session.totalCost && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Total Cost (incl. VAT):</strong> {formatOcpiCurrency(session.totalCost.incl_vat, session.currency)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Total Cost (excl. VAT):</strong> {formatOcpiCurrency(session.totalCost.excl_vat, session.currency)}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>

            {session.cdrToken && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.success.main }}>
                  Token Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Type:</strong> {session.cdrToken.type || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>ID Tag:</strong> {session.cdrToken.uid || 'N/A'}
                  </Typography>
                  {session.cdrToken.auth_method && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Auth Method:</strong> {session.cdrToken.auth_method}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* CDR Information - Only for CDR records */}
            {showCdrDetails && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.secondary.main }}>
                  CDR Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>CDR ID:</strong> {session.cdrId || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>CDR Currency:</strong> {session.cdrCurrency || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>CDR Total Energy:</strong> {session.cdrTotalEnergy ? formatOcpiEnergy(session.cdrTotalEnergy) : 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>CDR Last Updated:</strong> {session.cdrLastUpdated ? formatOcpiDate(session.cdrLastUpdated) : 'N/A'}
                      </Typography>
                      {session.cdrStatus && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>CDR Status:</strong>
                        </Typography>
                      )}
                      {session.cdrStatus && (
                        <Chip
                          label={session.cdrStatus}
                          size="small"
                          color={statusColorMap[session.cdrStatus]?.color}
                          variant="outlined"
                          sx={{
                            bgcolor: statusColorMap[session.cdrStatus]?.bgcolor,
                            color: statusColorMap[session.cdrStatus]?.text,
                            mt: 0.5
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                  {session.cdrTotalCost && (
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mb: 2, color: theme.palette.secondary.main }}>
                        CDR Total Cost
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {session.cdrTotalCost.incl_vat && (
                          <Typography variant="body2" color="text.secondary">
                             <strong>Total Cost (incl. VAT):</strong> {formatOcpiCurrency(session.cdrTotalCost.incl_vat, session.cdrCurrency)} (incl. VAT)
                          </Typography>
                        )}
                        {session.cdrTotalCost.excl_vat && (
                          <Typography variant="body2" color="text.secondary">
                             <strong>Total Cost (excl. VAT):</strong> {formatOcpiCurrency(session.cdrTotalCost.excl_vat, session.cdrCurrency)} (excl. VAT)
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    </Card>
    </>
  );
};

export default SessionCard;
